import gsap from "gsap";

/**
 * Transition de page "grid shutter".
 * Une grille de blocs couvre l'écran (scaleX 0 -> 1) en quittant une page,
 * puis se retire (scaleX 1 -> 0) en arrivant sur la nouvelle page.
 * Site multi-pages classique : on intercepte les liens internes et on relaie
 * l'état d'une page à l'autre via sessionStorage (évite tout flash au chargement).
 */
const ROWS = 4;
const COLS = 16;
const FLAG = "page-transition";
const LOADER_KEY = "loaderShown";
const DURATION = 0.6;
const STAGGER = 0.025;

export default class PageTransition {
	constructor() {
		this.container = document.querySelector(".js-page-transition");
		if (!this.container) return;

		this.blocks = [];
		this.covered = false;

		this.build();
		this.onResize = () => this.build();
		window.addEventListener("resize", this.onResize);

		this.bindLinks();
		this.playEnter();
	}

	build() {
		this.container.innerHTML = "";
		this.blocks = [];

		const w = window.innerWidth / COLS;
		const h = window.innerHeight / ROWS;

		for (let row = 0; row < ROWS; row++) {
			for (let col = 0; col < COLS; col++) {
				const block = document.createElement("span");
				block.className = "c-page-transition__block";
				block.style.width = `${w + 1}px`;
				block.style.height = `${h + 1}px`;
				block.style.left = `${col * w}px`;
				block.style.top = `${row * h}px`;
				block.style.transformOrigin = `${row % 2 === 0 ? "left" : "right"} center`;
				this.container.appendChild(block);
				this.blocks.push(block);
			}
		}

		gsap.set(this.blocks, { scaleX: this.covered ? 1 : 0 });
	}

	getRow(row) {
		return this.blocks.slice(row * COLS, row * COLS + COLS);
	}

	// Construit une timeline qui anime chaque rangée depuis un côté alterné.
	buildTimeline(target, onComplete) {
		const tl = gsap.timeline({ onComplete });

		for (let row = 0; row < ROWS; row++) {
			tl.to(
				this.getRow(row),
				{
					scaleX: target,
					duration: DURATION,
					ease: "power3.inOut",
					stagger: {
						each: STAGGER,
						from: row % 2 === 0 ? "start" : "end",
					},
				},
				"<"
			);
		}

		return tl;
	}

	// Recouvre l'écran (quitte la page en cours)
	cover(onComplete) {
		this.container.classList.add("is-active");
		this.covered = true;
		return this.buildTimeline(1, onComplete);
	}

	// Couvre l'écran instantanément (sans animation) — utilisé sous le loader au premier chargement.
	coverInstant() {
		if (!this.container) return;
		this.covered = true;
		gsap.set(this.blocks, { scaleX: 1 });
		this.container.classList.add("is-active");
	}

	// Découvre l'écran (arrive sur la nouvelle page)
	reveal(onComplete) {
		document.dispatchEvent(new CustomEvent("meral:transition-reveal-start"));

		if (!this.container) {
			onComplete?.();
			document.dispatchEvent(new CustomEvent("meral:transition-revealed"));
			return;
		}

		this.container.classList.add("is-active");
		this.buildTimeline(0, () => {
			this.container.classList.remove("is-active");
			this.covered = false;
			onComplete?.();
			document.dispatchEvent(new CustomEvent("meral:transition-revealed"));
		});
	}

	isCovered() {
		return this.covered;
	}

	playEnter() {
		let flagged = false;
		try {
			flagged = sessionStorage.getItem(FLAG) === "in";
			sessionStorage.removeItem(FLAG);
		} catch (e) {}

		if (flagged) {
			// Navigation interne : grille déjà couverte, on révèle la nouvelle page.
			window.scrollTo(0, 0);
			document.documentElement.classList.add("has-seen-loader");
			this.covered = true;
			gsap.set(this.blocks, { scaleX: 1 });
			this.container.classList.add("is-active");
			document.documentElement.classList.remove("is-transitioning");
			requestAnimationFrame(() => this.reveal());
			return;
		}

		// Premier chargement : grille sous le loader, le reveal est déclenché par Loader.
		let showLoader = true;
		try {
			showLoader = !sessionStorage.getItem(LOADER_KEY);
		} catch (e) {}

		if (showLoader) {
			this.coverInstant();
		}
	}

	bindLinks() {
		document.addEventListener("click", (e) => this.onClick(e));
	}

	onClick(e) {
		if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
			return;
		}

		const link = e.target.closest("a[href]");
		if (!link || !this.isInternal(link)) return;

		e.preventDefault();
		const url = link.href;

		try {
			sessionStorage.setItem(FLAG, "in");
		} catch (err) {}

		this.cover(() => {
			window.location.href = url;
		});
	}

	isInternal(link) {
		if (link.target && link.target !== "_self") return false;
		if (link.hasAttribute("download")) return false;
		if (link.dataset.noTransition !== undefined) return false;

		const href = link.getAttribute("href") || "";
		if (
			href.startsWith("#") ||
			href.startsWith("mailto:") ||
			href.startsWith("tel:") ||
			href.startsWith("javascript:")
		) {
			return false;
		}

		if (link.origin !== window.location.origin) return false;

		// Même page (ancre interne) : pas de transition
		if (link.pathname === window.location.pathname && link.hash) return false;

		return true;
	}

	destroy() {
		window.removeEventListener("resize", this.onResize);
	}
}
